import { useI18n } from '../../i18n';
// ============================================
// Forgot Password Page
// Email input for password reset request
// ============================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { env } from '../../config/env';
import { readJsonSafely, resolveApiErrorMessage } from '../../utils/http';

export function ForgotPasswordPage() {
  const { t } = useI18n();
  // BE nhận `identifier` = email hoặc số điện thoại, nên state không đặt tên `email` nữa.
  const [identifier, setIdentifier] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Call forgot password API — route đúng của BE là /auth/customer/forgot-password/
      // (bug #6: path cũ /auth/forgot-password trả 404 không phải JSON).
      //
      // Bug #7: BE nhận field `identifier` (email HOẶC số điện thoại), không phải
      // `email`. Gửi sai tên field thì request hợp lệ về mặt HTTP nhưng BE trả
      // "Dữ liệu không hợp lệ" — nhìn như lỗi email sai định dạng.
      const response = await fetch(`${env.apiBaseUrl}/auth/customer/forgot-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });

      // Giữ parse an toàn phòng body rỗng/non-JSON (proxy, 404 HTML, lỗi mạng...).
      // Format lỗi chung của API: { data, code, message, errors? } — đọc `message` ở root.
      const data = await readJsonSafely(response);

      if (!response.ok) {
        throw new Error(resolveApiErrorMessage(data, t('forgotPasswordFailed')));
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('forgotPasswordFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 px-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-800">{t('conaiStore')}</h1>
          <p className="mt-2 text-slate-600">{t('adminPortal')}</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-900">{t('forgotPassword')}</h2>
          <p className="mt-2 text-sm text-slate-600">
            {t('forgotPasswordSubtitle')}
          </p>

          {/* Error Message */}
          {error && (
            <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success ? (
            <div className="mt-6">
              <div className="rounded-lg bg-green-50 p-4">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">{t('emailSent')}</h3>
                    <p className="mt-1 text-sm text-green-700">
                      {t('resetLinkSentDesc')}
                    </p>
                  </div>
                </div>
              </div>
              <Link
                to={ADMIN_ROUTES.LOGIN}
                className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {t('backToLogin')}
              </Link>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="identifier" className="block text-sm font-medium text-slate-700">
                  {t('emailAddress')}
                </label>
                {/* type="text" chứ không phải "email": BE nhận cả số điện thoại,
                    để type="email" thì trình duyệt tự chặn SĐT ngay tại form. */}
                <input
                  id="identifier"
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  placeholder="your@email.com"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {t('sending')}
                  </span>
                ) : (
                  t('sendResetLink')
                )}
              </button>
            </form>
          )}

          {/* Back to Login Link */}
          {!success && (
            <p className="mt-6 text-center text-sm text-slate-600">
              {t('rememberPassword')}{' '}
              <Link to={ADMIN_ROUTES.LOGIN} className="font-medium text-blue-600 hover:text-blue-700">
                {t('backToLogin')}
              </Link>
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-slate-500">
          © 2024 ConAI. All rights reserved.
        </p>
      </div>
    </div>
  );
}
